/* eslint-disable react/jsx-no-useless-fragment */

import { ExtLink } from '../../../components/controls';
import { LinkButton } from '../../../components/navigation';
import { adminEmail } from '../addresses';

export const ProductHelp = {
  changingProduct: {
    label: 'Changing a product',
    text: (
      <>
        New accounts upload files with PulseCDN, so there is nothing to connect
        first. If your account uses its own storage, add your storage
        credentials first. Please also read how to upload files.
      </>
    ),
    url: 'https://pulseapp.notion.site/2e91ed4c26a9810a8808cbe0ef440318',
  },

  creatingProduct: {
    label: 'Creating a product',
    text: (
      <>
        New accounts upload files with PulseCDN, so there is nothing to connect
        first. If your account uses its own storage, add your storage
        credentials first. Please also read how to upload files.
      </>
    ),
    url: 'https://pulseapp.notion.site/2e91ed4c26a9810a8808cbe0ef440318',
  },

  enhancedCdn: {
    label: 'Use Enhanced CDN',
    text: (
      <>
        Enable{' '}
        <ExtLink href="https://www.notion.so/pulseapp/Using-the-CDN-3bc3339180e044feb619e5bd8c312528">
          CDN enhanced downloads
        </ExtLink>{' '}
        to reduce bandwidth costs and enhance download speeds for customers
      </>
    ),
  },

  files: {
    label: 'Files',
    text: (
      <>
        Please do not use spaces in your file URLs (use underscores instead), do
        not use symbols (like & or £) and try to keep files at 300-500MB per
        file.
      </>
    ),
  },

  fileUpdates: {
    generalUpdateHint: (
      <>
        Before creating product updates, please read{' '}
        <ExtLink href="https://pulseapp.notion.site/2e91ed4c26a9816580b6ecb727a1d1d1">
          this documentation
        </ExtLink>
        , otherwise you may create broken products.
      </>
    ),

    versionChanging: (
      <>
        IMPORTANT. Please read the following notes before making any changes to
        your product:
        <ol>
          <li>
            We do not suggest making any changes to a product on or close to a
            weekend as there is no support available during weekends. Support is
            only available Mon-Fri 9am to 5pm.
          </li>
          <li>
            <strong>Do not</strong>&nbsp;replace files in your hosting account
            with files of the exact same name as this will cause issues with
            downloads (we generate a hash file for each file URL so replacing
            files will break this). If you want to replace files and use the
            exact same name for the archives, then make sure the URL is
            different (eg. make the path v1.1/file1.rar etc.).
          </li>
          <li>
            Please{' '}
            <ExtLink href="https://pulseapp.notion.site/2e91ed4c26a9816580b6ecb727a1d1d1">
              read the Wiki documentation
            </ExtLink>{' '}
            about releasing product updates before doing so.
          </li>
          <li>
            Before releasing a product update or making any changes to a
            product, <strong>test this on a dummy product</strong> first to
            ensure it works OK.
          </li>
          <li>
            Changing the existing version may affect users who have already
            installed that version before. They will see the changed version as
            a new update in the app.
          </li>
        </ol>
      </>
    ),
  },

  generatePulseKeys: {
    label: 'Key generating',
    text: (
      <>
        Enter the number of keys you’d like to generate, choose a label to apply
        to the keys (
        <ExtLink href="https://www.notion.so/pulseapp/What-Are-Redemption-Key-Labels-af262301710a47a98dd4e0a48ddccdc9">
          what are labels?
        </ExtLink>
        ) if you want to, then click on “Generate”.
        <p>
          To copy the keys, click on the “<i>Copy To Clipboard</i>” button and
          copy them from the “<i>Unused</i>” box on the bottom of this page and
          place them wherever you’d like (eg. add to your store or send to a
          distributor).
        </p>
      </>
    ),
    url: 'https://www.notion.so/pulseapp/How-to-Generate-Redemption-Keys-748161a83f93476a86b92d4a0fce2397',
  },

  exportPulseKeys: {
    label: 'Key exporting',
    text: (
      <>
        Once you’ve added these keys somewhere outside of Pulse, mark them as
        “Distributed” so you know they’re placed somewhere that they might be
        sold or used in the future so are “unavailable” for use now (ie. you
        shouldn’t give them out anywhere else) but not yet redeemed
        <ExtLink href="https://www.notion.so/pulseapp/What-Does-Used-Unused-Distributed-Mean-7ccfccd0d80040198f984fe5cbea7480">
          (what does used/unused/distributed mean?)
        </ExtLink>
      </>
    ),
    topText: (
      <>
        These are the keys you have just generated. They will be added to your
        unused keys list at the bottom of this page until you mark them as
        distributed.
      </>
    ),
    url: 'https://www.notion.so/pulseapp/How-to-Generate-Redemption-Keys-e1906e964e364a509e99efe8198bd023',
  },

  extendedFeatures: {
    rulesVersionHint: (
      <>
        These instructions are performed for every version of your product, not
        just the latest version. So please ensure they are structured correctly
        to account for different versions.
      </>
    ),
    rulesWikiLink: (
      <>
        <ExtLink href="https://www.notion.so/pulseapp/How-to-Add-File-Move-Delete-Functionality-to-a-Product-deec255c36de483f9f1951e7cf484c39">
          How does this work?
        </ExtLink>
      </>
    ),
  },

  importSerials: {
    label: 'Importing existed serials to Pulse',
    text: (
      <>
        If your product uses a serial number for activation after installation
        (eg. its a plugin or Kontakt Player library), you can use the serial
        number as a Pulse redemption key too. This means the customer can use
        the same key for downloading in Pulse and activating the product
        elsewhere too to avoid confusion.
        <p>
          <b>Note</b>: If you mark serial numbers as distributed they will not
          be automatically sent to customers after purchase.
        </p>
      </>
    ),
    url: 'https://www.notion.so/pulseapp/Serial-Numbers-vs-Pulse-Keys-611801f9bb59458486255f6dbdc199c3',
  },

  readOnlyFields: {
    label: 'Read-only fields',
    text: (
      <>
        Some fields couldn&apos;t be changed when the Product is published.
        Contact the Pulse admin if you would wish to change it.
      </>
    ),
    action: (
      <LinkButton href={`mailto:${adminEmail}`} type="context">
        Contact Admin
      </LinkButton>
    ),
  },
};
